import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Upload, Search } from "lucide-react";
import api, { formatRp, imageUrl, formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const empty = {
  name: "", slug: "", category_id: "", description: "",
  price: 0, sale_price: null, cost_price: 0, stock: 0,
  weight_grams: 0, unit: "butir", sku: "", images: [],
  is_active: true, is_featured: false, is_bestseller: false,
};

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function ProductForm({ initial, categories, onSaved }) {
  const [form, setForm] = useState(initial || empty);
  const [uploading, setUploading] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.price = Number(payload.price);
      payload.sale_price = payload.sale_price === "" || payload.sale_price === null ? null : Number(payload.sale_price);
      payload.cost_price = Number(payload.cost_price);
      payload.stock = Number(payload.stock);
      payload.weight_grams = Number(payload.weight_grams);
      if (!payload.slug) payload.slug = slugify(payload.name);
      if (initial?.id) await api.put(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Produk tersimpan");
      onSaved?.();
    } catch (e) { notifyError(e); }
  };

  const uploadImg = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("images", [...(form.images || []), data.path]);
    } catch (e) { notifyError(e); } finally { setUploading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Nama Produk</Label><Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="prod-name" /></div>
        <div><Label>Slug URL</Label><Input value={form.slug} onChange={(e) => upd("slug", e.target.value)} placeholder={slugify(form.name)} data-testid="prod-slug" /></div>
        <div>
          <Label>Kategori</Label>
          <Select value={form.category_id} onValueChange={(v) => upd("category_id", v)}>
            <SelectTrigger data-testid="prod-category"><SelectValue placeholder="Pilih..." /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={4} data-testid="prod-desc" /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Harga (Rp)</Label><Input type="number" value={form.price} onChange={(e) => upd("price", e.target.value)} required data-testid="prod-price" /></div>
        <div><Label>Harga Diskon</Label><Input type="number" value={form.sale_price ?? ""} onChange={(e) => upd("sale_price", e.target.value)} placeholder="-" data-testid="prod-sale" /></div>
        <div><Label>Harga Modal</Label><Input type="number" value={form.cost_price ?? 0} onChange={(e) => upd("cost_price", e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div><Label>Stok</Label><Input type="number" value={form.stock} onChange={(e) => upd("stock", e.target.value)} required data-testid="prod-stock" /></div>
        <div><Label>Berat (gr)</Label><Input type="number" value={form.weight_grams} onChange={(e) => upd("weight_grams", e.target.value)} /></div>
        <div>
          <Label>Satuan</Label>
          <Select value={form.unit} onValueChange={(v) => upd("unit", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="butir">Butir</SelectItem>
              <SelectItem value="kg">Kg</SelectItem>
              <SelectItem value="tray">Tray</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>SKU</Label><Input value={form.sku || ""} onChange={(e) => upd("sku", e.target.value)} /></div>
      </div>
      <div>
        <Label>Gambar Produk</Label>
        <div className="mt-2 flex gap-2 flex-wrap">
          {form.images?.map((img, i) => (
            <div key={`${img}-${i}`} className="w-20 h-20 relative rounded-xl overflow-hidden border border-[#E5E5E5]">
              <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => upd("images", form.images.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-white/95 rounded-full p-0.5 text-[#DC2F02]"><Trash2 size={12} /></button>
            </div>
          ))}
          <label className="w-20 h-20 rounded-xl border-2 border-dashed border-[#E5E5E5] grid place-items-center cursor-pointer hover:bg-[#FAF9F6]">
            <Upload size={20} className="text-[#5C5C5C]" />
            <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadImg(e.target.files[0])} data-testid="prod-upload-img" />
          </label>
        </div>
        {uploading && <div className="text-xs text-[#5C5C5C] mt-1">Mengupload...</div>}
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2">
        <Label className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => upd("is_active", v)} /> Aktif</Label>
        <Label className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={(v) => upd("is_featured", v)} /> Unggulan</Label>
        <Label className="flex items-center gap-2"><Switch checked={form.is_bestseller} onCheckedChange={(v) => upd("is_bestseller", v)} /> Terlaris</Label>
      </div>
      <DialogFooter className="pt-3">
        <Button type="submit" className="brand-btn-primary rounded-xl" data-testid="prod-save-btn">Simpan Produk</Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    api.get("/products", { params: { include_inactive: true, limit: 200, search: search || undefined } })
      .then((r) => setProducts(r.data.items));
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const confirmDelete = async () => {
    if (!toDelete?.id) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/products/${toDelete.id}`);
      // Optimistic UI update: remove from local list immediately
      setProducts((prev) => prev.filter((p) => p.id !== toDelete.id));
      const msg = data?.delete_type === "soft"
        ? "Produk berhasil dihapus (soft delete — terkait riwayat pesanan)"
        : "Produk berhasil dihapus";
      toast.success(msg);
      setToDelete(null);
      // Refresh from server to ensure consistency
      load();
    } catch (e) {
      toast.error(`Gagal menghapus produk: ${formatApiError(e)}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black">Produk</h1>
          <p className="text-sm text-[#5C5C5C]">{products.length} produk</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><Button className="brand-btn-primary rounded-xl" data-testid="add-product-btn"><Plus size={14} className="mr-1" /> Tambah Produk</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Produk</DialogTitle></DialogHeader>
            <ProductForm initial={editing} categories={categories} onSaved={() => { load(); setOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-2xl">
        <div className="p-4 border-b border-[#E5E5E5]">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama produk..." className="pl-9 rounded-xl" data-testid="prod-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                <th className="px-4 py-3">Produk</th>
                <th className="px-4 py-3">Harga</th>
                <th className="px-4 py-3">Stok</th>
                <th className="px-4 py-3">Terjual</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6]" data-testid={`prod-row-${p.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FAF9F6] overflow-hidden shrink-0">
                        {p.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold line-clamp-1">{p.name}</div>
                        <div className="text-xs text-[#5C5C5C]">{p.sku || "-"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-bold text-[#E85D04]">
                    {formatRp(p.sale_price || p.price)}
                    {p.sale_price && <div className="text-xs text-[#5C5C5C] line-through font-normal">{formatRp(p.price)}</div>}
                  </td>
                  <td className="px-4 py-3">{p.stock} {p.unit}</td>
                  <td className="px-4 py-3">{p.sold_count || 0}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${p.is_active ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-red-100 text-red-700"}`}>
                      {p.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }} className="rounded-lg" data-testid={`edit-prod-${p.slug}`}><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setToDelete(p)} className="rounded-lg text-[#DC2F02] hover:bg-red-50 hover:text-[#DC2F02]" data-testid={`del-prod-${p.slug}`}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && !deleting && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda akan menghapus <b>{toDelete?.name}</b>. Jika produk pernah dipesan, data akan disimpan
              sebagai arsip (soft delete) untuk menjaga riwayat pesanan, tetapi tidak akan tampil lagi
              di mana pun. Tindakan ini akan dicatat ke audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="confirm-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-[#DC2F02] hover:bg-[#b02701]"
              data-testid="confirm-delete-ok"
            >
              {deleting ? "Menghapus..." : "Ya, hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
